import { toIsoString, sha256 } from './logic.js'

const jsonHeaders = { 'Accept': '*/*', 'Content-Type': 'application/json' }

export default class SeezSdk extends EventTarget {
  #loggingOut = false
  #handoutToken = null
  #accessToken = null
  #refreshPromise = null
  #alreadyChecked = false
  #baseHeaders = { ...jsonHeaders }
  #favorites = JSON.parse(localStorage.getItem('favorites')) ?? []
  #savedSearches = JSON.parse(localStorage.getItem('searches')) ?? []
  #lastUserId = null // to deduplicate analytics identify tracking
  #userSubscriptionHandlers = []
  anonymousId = null

  constructor() {
    super()
    this.targetSite = {}
    this.clientId = document?.querySelector('[data-seez-client-id]')?.getAttribute('data-seez-client-id')
    if (this.clientId) this.#baseHeaders['Client-Id'] = this.clientId
    this.#parseHandoutFromUrl()
    this.anonymousId = localStorage.getItem('Seez-Anonymous-Id')
    if (this.anonymousId == null) {
      this.anonymousId = crypto.randomUUID()
      localStorage.setItem('Seez-Anonymous-Id', this.anonymousId)
    }
    this.#baseHeaders['Seez-Anonymous-Id'] = this.anonymousId
    this.setSessionId()
    this.#baseHeaders['Client-Lang'] = document.querySelector('html')?.getAttribute('lang')
    this.#getCustomizations()
  }

  setSessionId() {
    let sessionId = sessionStorage.getItem('Seez-Session-Id')
    if (sessionId == null) {
      const cookies =
        document.cookie
          ?.split('; ')
          .map(p => p.split('='))
          .reduce((t, c) => ({ ...t, [c[0]]: c[1] }), {}) ?? {}
      sessionId = cookies['seez-session-id'] ?? crypto.randomUUID()

      sessionStorage.setItem('Seez-Session-Id', sessionId)
    }
    this.#baseHeaders['Seez-Session-Id'] = sessionId
    return sessionId
  }

  //#region language
  #languageResources = {}
  loadLanguageResources(language) {
    if (!(language in this.#languageResources)) {
      const url = `${import.meta.env.VITE_SEEZ_BASE_URL}/cms/${language}/sdk` + (this.clientId ? `/${this.clientId}` : '')
      this.#languageResources[language] = fetch(url)
        .then(r => r.json())
        .catch(e => console.error(e))
    }
    return this.#languageResources[language]
  }
  //#endregion

  //#region modals
  showMessage(content, closable = true) {
    let promiseResolve = null

    function closed() {
      document.body.querySelectorAll('seez-sdk-modal').forEach(x => x.parentNode.removeChild(x))
      if (promiseResolve) promiseResolve()
    }

    const modalComponent = document.createElement('seez-sdk-modal')
    modalComponent.setAttribute('closable', closable)
    modalComponent.innerHTML = content
    document.body.appendChild(modalComponent)
    modalComponent.addEventListener('close', closed)

    // eslint-disable-next-line no-unused-vars
    return new Promise(function (resolve, reject) {
      promiseResolve = resolve
    }, false)
  }

  async showModal(tag, props = {}, closable = true, slots = {}) {
    let promiseResolve = null

    function closed(result) {
      document.body.removeChild(result.target)
      if (promiseResolve) promiseResolve(result.detail[0])
    }

    if (tag.startsWith('seez-sdk-')) await this.#loadModuleFor(tag)

    const modalComponent = document.createElement('seez-sdk-modal')
    modalComponent.setAttribute('closable', closable)
    const component = document.createElement(tag)
    for (const key in props) component.setAttribute(key, props[key])
    for (const key in slots) {
      const s = document.createElement('div')
      s.setAttribute('slot', key)
      s.innerHTML = slots[key].startsWith('#') ? document.querySelector(slots[key])?.innerHTML : slots[key]
      component.appendChild(s)
    }
    modalComponent.appendChild(component)

    document.body.appendChild(modalComponent)
    modalComponent.addEventListener('close', closed.bind(this))

    // eslint-disable-next-line no-unused-vars
    return new Promise(function (resolve, reject) {
      promiseResolve = resolve
    }, false)
  }

  async #loadModuleFor(componentName) {
    if (!componentName.startsWith('seez-sdk-')) return

    const modules = {
      cars: ['chat-widget', 'chat-fullscreen'],
      seezar: ['wizard', 'loader', 'seezar-modal', 'contact-button', 'carousel', 'clear-chat', 'dynamic-inquiry-form', 'comparison-table', 'lead-form', 'test-drive-form', 'contact-point', 'location-cards', 'seezar-chat'],
      enterprise: ['modal', 'form-template', 'form-template-address', 'login-form', 'logout', 'listing-card', 'trade-in', 'tradein-help', 'loan-calculator', 'financing-provider', 'carousel', 'favorites', 'orders', 'active-order-cancellation', 'active-order-pause', 'unavailable-listing-modal', 'cancel-order-modal', 'saved-searches', 'search', 'logout', 'user-profile', 'listing-details', 'trade-in-form', 'lead-form', 'test-drive-modal', 'test-drive-form', 'dynamic-inquiry-form', 'loader', 'tradein-offer', 'tradeins', 'buying-flow', 'delete-user', 'mini-listing']
    }

    const moduleName = Object.entries(modules).find(([, components]) => components.includes(componentName.substring(9)))?.[0]
    if (moduleName == null) return
    await this.#injectScriptTag(`${import.meta.env.VITE_SEEZ_BASE_URL}/sdk/seez-sdk-${moduleName}.js`)
  }

  async showComponent(componentName, options = {}) {
    await this.#loadModuleFor(componentName)
    const c = document.createElement(componentName)
    const dialog = document.createElement('dialog')
    const closeFunction = () => {
      dialog.close()
      document.body.removeChild(dialog)
    }

    dialog.className = 'seezDialog'
    if (options.modal) dialog.classList.add('seezModal')
    if (options.styles) Object.assign(dialog.style, options.styles)
    dialog.appendChild(c)
    // const x = document.createElement('button')
    // x.textContent = '×'
    // x.onclick = closeFunction
    // dialog.appendChild(x)
    document.body.appendChild(dialog)

    options.modal ? dialog.showModal() : dialog.show()
    return { dialog, component: c, closeFunction }
  }
  //#endregion

  //#region Session
  async requestCode(email, language) {
    const url = `${import.meta.env.VITE_SEEZ_BASE_URL}/auth/otp?email=${encodeURIComponent(email)}${language ? `&language=${language}` : ''}`
    const requestOTPResponse = await fetch(url)
    return requestOTPResponse.json()
  }

  async loginWithOTP(email, otp, acceptsMarketing, phone) {
    if ((email && phone) || (!email && !phone)) throw new Error('Must provide only email or phoneNumber.')
    const body = { otp, marketing: acceptsMarketing }
    if (email) body.email = email
    else if (phone) body.phone = phone

    const response = await fetch(import.meta.env.VITE_SEEZ_BASE_URL + '/auth/authenticate', { method: 'POST', headers: this.#baseHeaders, body: JSON.stringify(body) })
    const { valid, refreshToken, accessToken } = await response.json()
    if (!valid) throw new Error('Invalid email/OTP')

    if (refreshToken != null) {
      localStorage.setItem('refresh_token', refreshToken)
      this.#accessToken = this.#isTokenValid(accessToken) ? accessToken : null
    }
    await this.#retrieveUserDetails()
    this.closeLogin()
    await this.#invokeHandlers()
  }

  #parseHandoutFromUrl() {
    var url = new URL(window.location)
    const ht = url.searchParams.get('ht')
    if (ht && this.#isTokenValid(ht)) {
      this.#handoutToken = ht
      url.searchParams.delete('ht')
      if (url.hash === '#_=_') url.hash = ''
      window.history.replaceState({}, '', url.toString())
      this.#retrieveUserDetails()
    }
  }

  #clearAuthState() {
    this.#accessToken = null
    this.#handoutToken = null
    localStorage.removeItem('refresh_token')
    this.#alreadyChecked = true
    this.#invokeHandlers()
  }

  async logout(redirectUrl) {
    this.#loggingOut = true
    this.#accessToken = null
    this.#handoutToken = null
    this.#alreadyChecked = true

    if (this.#refreshPromise) {
      await this.#refreshPromise.catch(error => console.error('In-flight token refresh failed during logout:', error))
    }
    this.#refreshPromise = null

    const trackingPromise = this.track('logout')
    this.#cleanUserDetails()
    localStorage.removeItem('refresh_token')
    await trackingPromise

    if (redirectUrl) window.location = redirectUrl
    else window.location.reload()
  }

  showLogin(bannerState, closable = true, slots, props = null) {
    return this.showModal('seez-sdk-login-form', props, closable, slots)
  }

  closeLogin() {
    document.body.querySelectorAll('seez-sdk-modal').forEach(x => x.parentNode.removeChild(x))
  }

  async showLogout(redirectUrl) {
    let promiseResolve = null

    function closed(result) {
      document.body.querySelectorAll('seez-sdk-logout').forEach(x => x.parentNode.removeChild(x))
      if (result.detail[0] === true)
        this.logout(redirectUrl).then(() => {
          if (promiseResolve) promiseResolve(result.detail[0])
        })
      else if (promiseResolve) promiseResolve(result.detail[0])
    }

    await this.#loadModuleFor('seez-sdk-logout')
    const logoutComponent = document.createElement('seez-sdk-logout')
    document.body.appendChild(logoutComponent)
    logoutComponent.addEventListener('close', closed.bind(this))

    // eslint-disable-next-line no-unused-vars
    return new Promise(function (resolve, reject) {
      promiseResolve = resolve
    }, false)
  }

  async #refreshAccessToken() {
    if (this.#loggingOut) return null

    if (this.#isTokenValid(this.#handoutToken)) {
      const payload = { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ handoutToken: this.#handoutToken }) }
      const response = await fetch(import.meta.env.VITE_SEEZ_BASE_URL + '/auth/handout', payload)

      if (this.#loggingOut) return null

      if (!response.ok) {
        console.error('Failed to refresh access token')
        this.#clearAuthState()
        return null
      }

      const { refreshToken } = await response.json()

      if (this.#loggingOut) return null

      if (!this.#isTokenValid(refreshToken)) {
        console.error('Received invalid refresh token')
        this.#clearAuthState()
        return null
      }

      localStorage.setItem('refresh_token', refreshToken)
      this.#handoutToken = null
    }
    try {
      const rt = localStorage.getItem('refresh_token')
      if (rt) {
        const payload = { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ refreshToken: rt }) }
        const refreshResponse = await fetch(import.meta.env.VITE_SEEZ_BASE_URL + '/auth/refresh', payload)
        if (this.#loggingOut) return null

        const accessTokenRes = await refreshResponse.text()
        if (this.#loggingOut) return null

        if (accessTokenRes.trim().startsWith('{')) {
          // Response body is JSON (NEW /refresh response)
          const { accessToken, refreshToken } = JSON.parse(accessTokenRes)
          if (refreshToken != null) localStorage.setItem('refresh_token', refreshToken)
          this.#accessToken = this.#isTokenValid(accessToken) ? accessToken : null
        } else this.#accessToken = this.#isTokenValid(accessTokenRes) ? accessTokenRes : null // rsponse body is raw accessBody as string (OUTDATED /refresh response)
        this.#invokeHandlers()
      }
    } catch (error) {
      this.#accessToken = null
      this.#invokeHandlers()
      console.error(error)
    }
    this.#alreadyChecked = true
    return this.#accessToken
  }

  #parseJwt(jwt) {
    try {
      const base64Url = jwt.split('.')[1]
      const base64 = base64Url.replace('-', '+').replace('_', '/')
      const claimsString = atob(base64)
      return JSON.parse(claimsString)
    } catch {
      return null
    }
  }

  #isTokenValid(token) {
    try {
      const jwtRegEx = /^[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*$/
      if (token == null || !jwtRegEx.test(token)) return false
      const { exp } = this.#parseJwt(token)
      const now = Math.floor((new Date().getTime() + 1) / 1000)
      if (exp - now < 30) return false
      return true
    } catch {
      return false
    }
  }

  getAccessToken(forceRefresh) {
    if (this.#loggingOut) return null

    // unify promise
    if (forceRefresh) {
      this.#alreadyChecked = false
      this.#accessToken = null
    }

    if (this.#alreadyChecked && this.#accessToken == null) return null
    if (this.#isTokenValid(this.#accessToken)) return this.#accessToken
    if (this.#refreshPromise == null) {
      this.#refreshPromise = this.#refreshAccessToken()
      this.#refreshPromise.then(() => (this.#refreshPromise = null))
    }
    return this.#refreshPromise
  }

  async getCurrentUser(forceRefresh, track = true) {
    const at = await this.getAccessToken(forceRefresh)
    const user = this.#parseJwt(at)
    if (track) {
      if (user) this.identify({ ...user, loginStatus: true, userId: user.id, phone: user.phone ?? null })
      else if (this.#lastUserId != null) {
        this.#lastUserId = null
        this.dispatchEvent(new CustomEvent('trackingReset', { bubbles: true, composed: true }))
      }
    }
    return user
  }

  #getCurrentUserSync() {
    //gets the user without fetching (returns null if it's expired)
    if (this.#isTokenValid(this.#accessToken)) return this.#parseJwt(this.#accessToken)
    return null
  }

  userSubscribe(callback) {
    this.#userSubscriptionHandlers.push(callback)
    this.getAccessToken()
  }

  userUnsubscribe(callback) {
    const index = this.#userSubscriptionHandlers.indexOf(callback)
    if (index > -1) this.#userSubscriptionHandlers.splice(index, 1)
  }

  async #invokeHandlers() {
    if (this.#userSubscriptionHandlers.length === 0) return
    const user = this.#getCurrentUserSync()
    await Promise.allSettled(this.#userSubscriptionHandlers.map(h => h(user)))
  }
  //#endregion

  //#region permissions
  checkAccess(c) {
    const user = this.#getCurrentUserSync()
    if (!user) return { hasPermission: false, scopes: [] }

    //super user case
    const roles = Array.isArray(user.roles) ? user.roles : []
    if (roles.some(r => r?.rolecode?.toLowerCase() === 'supusr')) return { hasPermission: true, isSuper: true, scopes: [] }

    if (typeof c !== 'string') return { hasPermission: false, scopes: [] }
    const code = c.toLowerCase()

    // user.perm case — pass through backend scope as-is
    if (Array.isArray(user.perm)) {
      const permScopes = user.perm.filter(p => p.permissions?.some(permission => permission?.toLowerCase?.() === code)).map(p => ({ ...(p.scope || {}) }))
      if (permScopes.length > 0) return { hasPermission: true, scopes: permScopes }
    }

    // TODO: Remove this block once the backend 'p' key is deprecated.
    // user.p case — pass through as-is
    const legacyPermission = user.p?.[c] || user.p?.[code]
    if (legacyPermission) {
      return { hasPermission: true, scopes: [{ ...(legacyPermission || {}) }] }
    }

    if (roles.some(r => r?.rolecode?.toLowerCase() === 'pii') && code === 'pii') return { hasPermission: true, isPii: true, scopes: [] }
    // user.roles only: normalize to same shape as perm `convertToOldFormat` scope entries (dealershipid + targetsite_id arrays)
    const matchingRoles = roles.filter(r => r?.rolecode?.toLowerCase() === code)
    if (matchingRoles.length > 0) {
      const normalizeScopeIds = scope =>
        Object.fromEntries(
          Object.entries(scope ?? {}).map(([key, value]) => {
            const shouldArrayWrap = value != null && !Array.isArray(value) && key?.toLowerCase?.().endsWith('id')
            return [key, shouldArrayWrap ? [value] : value]
          })
        )
      const mergeScopes = scopes => {
        const merged = {}
        for (const scope of scopes ?? []) {
          if (scope == null || typeof scope !== 'object') continue
          for (const [key, value] of Object.entries(scope)) {
            if (value == null) continue
            const asArray = Array.isArray(value) ? value : [value]
            merged[key] = Array.from(new Set([...(merged[key] ?? []), ...asArray]))
          }
        }
        return merged
      }
      //need tihs to look the same shape as scopes under perm and p
      const normalizeData = merged => {
        const d = merged.dealershipid
        const dealershipid = Array.isArray(d) ? [...new Set(d)] : d != null ? [d] : []
        const t = [...(Array.isArray(merged.target_siteid) ? merged.target_siteid : []), ...(Array.isArray(merged.targetsite_id) ? merged.targetsite_id : [])]
        const targetsite_id = [...new Set(t)]
        return { dealershipid, targetsite_id }
      }

      const roleScopes = matchingRoles
        .map(r => {
          if (r?.scope && typeof r.scope === 'object' && Object.keys(r.scope).length > 0) return normalizeScopeIds({ ...(r.scope || {}) })
          const fallbackScope = Object.fromEntries(Object.entries(r ?? {}).filter(([key]) => key !== 'scope' && key !== 'rolecode' && key !== 'permissions'))
          return normalizeScopeIds(fallbackScope)
        })
        .filter(Boolean)
      return { hasPermission: true, scopes: [normalizeData(mergeScopes(roleScopes))] }
    }
    return { hasPermission: false, scopes: [] }
  }
  //#endregion

  //#region api
  async queryApi(query, variables, signal) {
    const at = await this.getAccessToken()
    const body = { query: query.replace(/[\n,\s]\s*/gm, ' ') }
    if (variables) body.variables = variables
    const request = {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        ...this.#baseHeaders,
        'Local-Time': toIsoString(new Date())
      }
    }
    if (at) request.headers.authorization = `Bearer ${at}`
    if (signal) request.signal = signal

    const response = await fetch(`${import.meta.env.VITE_SEEZ_BASE_URL}/api`, request)
    const result = await response.json()
    result.warnings?.forEach(console.warn)
    if (result.errors) throw new Error(result.errors.map(e => e.message).join('\r\n'))
    return result.data
  }

  async subscribeApi(query, callback) {
    const at = await this.getAccessToken()
    const ws = new WebSocket(import.meta.env.VITE_SEEZ_API_WS)
    ws.onmessage = m => callback(JSON.parse(m.data))
    const payload = { query, clientId: this.clientId }
    if (at) payload.authorization = 'Bearer ' + at
    ws.onopen = () => ws.send(JSON.stringify(payload))
    return ws
  }
  //#endregion

  //#region Favorites
  getFavorites() {
    return this.#favorites
  }

  async #persistFavorites() {
    localStorage.setItem('favorites', JSON.stringify(this.#favorites))
    if (await this.getCurrentUser(null, false)) {
      const result = await this.queryApi('mutation sf($ids: [ID]) {saveAllFavorites(listingIds: $ids)}', { ids: this.#favorites })
      const savedIds = result.saveAllFavorites.map(x => parseInt(x))
      localStorage.setItem('favorites', JSON.stringify(savedIds))
    }
  }

  setFavorite(listingId, favorited) {
    //if not favorited is specified 'toggle' is assumed
    const id = parseInt(listingId)
    const isFavorited = typeof favorited === 'boolean' ? favorited : !this.#favorites.includes(id)
    if (isFavorited === false) {
      this.#favorites = this.#favorites.filter(x => x !== id)
      this.#persistFavorites()
    } else if (!this.#favorites.includes(id)) {
      const newList = [...this.#favorites, id]
      newList.sort()
      this.#favorites = newList
      this.#persistFavorites()
    }
    return isFavorited
  }
  //#endregion

  //#region Saved Searches
  getSavedSearches() {
    return this.#savedSearches
  }

  #persistsSavedSearches() {
    localStorage.setItem('searches', JSON.stringify(this.#savedSearches))
    window.dispatchEvent(
      new CustomEvent('saved-searches-changed', {
        detail: {
          numSearch: this.#savedSearches?.length
        }
      })
    )
  }

  async addSearchInServer(name, filter) {
    const query = 'mutation saveSearch($name: String!, $filter: ListingFiltersInput!) { saveSearch(searchName: $name, filterParameters: $filter) {id} }'
    const { saveSearch } = await window.seezSdk.queryApi(query, { name: name, filter: filter })
    const newId = saveSearch[saveSearch.length - 1].id
    return newId
  }

  async addSearch(name, filter) {
    const minId = this.#savedSearches.reduce((t, c) => Math.min(c.id, t), 0)
    const newList = [...this.#savedSearches, { id: minId - 1, name: name, filterParameters: filter }] // creates negative index so the server knows it should be 'seq'
    this.#savedSearches = newList
    this.#persistsSavedSearches()
    const track = false
    if (await this.getCurrentUser(null, track)) {
      newList[newList.length - 1].id = await this.addSearchInServer(name, filter)
      this.#savedSearches = newList
      this.#persistsSavedSearches()
    }
  }

  async removeSearch(id) {
    this.#savedSearches = this.#savedSearches.filter(x => parseInt(x.id) !== parseInt(id))
    this.#persistsSavedSearches()
    if ((await this.getCurrentUser()) && id > 0) {
      const { removeSearch } = await window.seezSdk.queryApi(`mutation {removeSearch(id: ${id}) {id}}`)
      const serverIds = removeSearch.map(r => parseInt(r.id))
      this.#savedSearches = this.#savedSearches.filter(x => serverIds.includes(parseInt(x.id)))
      this.#persistsSavedSearches()
    }
  }

  static vueSavedSearchesMixin = {
    data() {
      return {
        savedSearches: window.seezSdk.getSavedSearches()
      }
    },
    methods: {
      addSearch(name, filter) {
        window.seezSdk.addSearch(name, filter)
        this.savedSearches = window.seezSdk.getSavedSearches()
      },
      removeSearch(id) {
        window.seezSdk.removeSearch(id)
        this.savedSearches = window.seezSdk.getSavedSearches()
      }
    }
  }

  async #retrieveUserDetails() {
    if ((await this.getCurrentUser()) == null) return
    const { user } = await this.queryApi('{user{favorites {listingId},savedSearches{id,name,filterParameters{bodyTypes,brands,colors,driveTypes,engineSizeMax,freeText,fuelTypes,kilometrageMin,kilometrageMax,models,numberOfDoorsMax,numberOfDoorsMin,priceMin,priceMax,priceType,sort,transmissions,yearMin,yearMax}}}}')

    const userFavorites = user.favorites.map(l => parseInt(l.listingId))
    const newLocalFavorites = this.getFavorites().filter(f => !userFavorites.includes(f))
    if (newLocalFavorites.length > 0) {
      userFavorites.push(...newLocalFavorites)
      userFavorites.sort()
      this.#favorites = userFavorites
      this.#persistFavorites()
    } else {
      this.#favorites = userFavorites
      localStorage.setItem('favorites', JSON.stringify(this.#favorites))
    }

    const searches = user.savedSearches
    const newLocalSearches = this.getSavedSearches().filter(s => !searches.some(x => x.name === s.name) && s.id < 0)
    this.#savedSearches = searches
    for (const newSearch of newLocalSearches) {
      this.addSearch(newSearch.name, newSearch.filterParameters)
    }
    this.#persistsSavedSearches()
    this.#invokeHandlers()
  }

  #cleanUserDetails() {
    this.#favorites = []
    localStorage.removeItem('favorites')
    this.#savedSearches = []
    localStorage.removeItem('searches')
  }
  //#endregion

  //#region Analytics
  track(eventKey, properties, vData) {
    this.dispatchEvent(new CustomEvent('track', { bubbles: true, composed: true, detail: { eventKey, properties: { ...properties, sessionId: this.#baseHeaders?.['Seez-Session-Id'] }, vData } }))
    return Promise.allSettled([this.#internalTrack('track', eventKey, properties, vData?.id), this.#ga4Track(eventKey, properties)])
  }

  trackNavigation(url) {
    this.#internalTrack('page', 'navigation', { url: url ?? window.location.href })
  }

  async #internalTrack(eventType, eventName, details, listingId) {
    const messageBody = {
      eventType,
      eventName,
      clientId: this.clientId,
      url: window?.location?.href,
      occuredAt: toIsoString(new Date()),
      details: details ? Object.fromEntries(Object.entries(details).filter(p => p[1] != null && p[1] !== '')) : {}, // remove null or empty values
      listingId,
      anonymousId: this.#baseHeaders?.['Seez-Anonymous-Id'],
      sessionId: this.#baseHeaders?.['Seez-Session-Id'],
      userAgent: navigator?.userAgent,
      languages: navigator?.languages
    }

    messageBody.userId = this.#loggingOut ? this.#lastUserId : await this.getCurrentUser().then(u => u?.id)

    const payload = {
      'Action': 'SendMessage',
      'MessageAttributes.1.Name': 'operation',
      'MessageAttributes.1.Value.DataType': 'String',
      'MessageAttributes.1.Value.StringValue': 'activityTrack',
      'MessageBody': JSON.stringify(messageBody)
    }

    const body = new URLSearchParams(payload)
    // navigator.sendBeacon(import.meta.env.VITE_SQS_ANALYTICS, body)
    await fetch(import.meta.env.VITE_SEEZ_BASE_URL + import.meta.env.VITE_SQS_ANALYTICS, { method: 'POST', body })
  }

  #ga4Track(eventName, details) {
    if (details?.internal_event === true) return
    const props = { ...(details?.items?.length === 1 ? details.items[0] : {}), ...(this.targetSite.trackingParams ?? {}), ...details }
    if (window.seezExternalTracking && typeof window.seezExternalTracking === 'function') window.seezExternalTracking(eventName, props)
    else {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, props)
      else if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: eventName, ...props })
    }
  }

  //#region Segment
  async identify({ userId, name, email, marketing, loginStatus, phone }) {
    if (this.#lastUserId === userId) return

    this.#lastUserId = userId
    const payload = {
      userId,
      name,
      email,
      hashedEmail: await sha256(email),
      loginStatus,
      marketing_consent: marketing === 1,
      phone
    }

    this.dispatchEvent(new CustomEvent('identify', { bubbles: true, composed: true, detail: { userId, ...payload } }))
    return this.#internalTrack('identify', 'identify', payload)
  }
  //#endregion
  //#endregion

  //#region customizations
  async #getCustomizations() {
    if (this.clientId == 'pad') this.targetSite = { sdkModules: ['seezar', 'enterprise'] }
    else if (this.clientId?.length)
      this.targetSite = await fetch(`${import.meta.env.VITE_SEEZ_BASE_URL}/config/ts/${this.clientId}`)
        .then(r => r.json())
        .catch(error => {
          console.error('Failed to fetch customizations:', error)
          return {}
        })

    await this.#loadModules()

    for (const scriptName of this.targetSite.customScripts ?? []) await this.#injectScriptTag(`${import.meta.env.VITE_SEEZ_BASE_URL}/integrations/${scriptName}`)
    if (this.targetSite.customCss?.length) document.dispatchEvent(new CustomEvent('customStylesUpdated'))
    this.dispatchEvent(new CustomEvent('targetSiteLoaded', { detail: this.targetSite }))
  }

  async #loadModules() {
    if (!this.targetSite.sdkModules?.length) return

    const skip =
      document
        ?.querySelector('[data-skip-modules]')
        ?.getAttribute('data-skip-modules')
        ?.split(/[,;\s]/g) ?? [] // this is to avoid loading server modules when debugging locally
    const modules = this.targetSite.sdkModules.filter(m => !skip.includes(m))
    await Promise.all(modules.map(m => this.#injectScriptTag(`${import.meta.env.VITE_SEEZ_BASE_URL}/sdk/seez-sdk-${m}.js`)))
  }
  //#endregion

  //#region modules
  #injectScriptTag(url) {
    return new Promise((resolve, reject) => {
      // Check if the script is already present in the document
      if (document.querySelector(`script[src="${url}"]`)) {
        resolve() // If it exists, simply resolve and don't add it again
        return
      }

      const script = document.createElement('script')
      script.onload = resolve
      script.onerror = reject
      script.type = 'module'
      script.defer = true
      script.src = url
      document.head.appendChild(script)
    })
  }
  //#endregion
}
